/** @jsxImportSource @emotion/react */
import { forwardRef, useState } from 'react';

import type { ApiPropertyProject, ApiUpdatePropertyProject } from '@kaiizen/kai-pro-api';
import { formatCurrency } from '@kaiizen/kai-reports';

import { useToast } from '@/Client/State/ToastContext';
import { RowKebabMenu, type RowKebabMenuItem } from '@/Client/Widgets/RowKebabMenu';
import { DEFAULT_PROPERTY_JOBS_SECTION_STYLES, type PropertyJobsSectionStyles } from './propertyJobsSectionStyles';
import {
  getPropertyJobCounts,
  getPropertyJobScopeMoney,
  getPropertyJobsForView,
  getPropertyJobsLifetimeTotal,
  getStackedDate,
  type PropertyJobsView,
} from './propertyOverviewLib';

export const NEW_JOB_LABEL = '+ New job';
const STATUS_LABEL = { active: 'Active', completed: 'Completed' } as const;

/**
 * The property's jobs (its projects): Active or Archived, newest update first (Updated sorts either
 * way), each opening its project. A job's menu archives or unarchives it and completes or
 * reactivates it. Scope dollars come from each job's embedded scan totals, not line-item diffs.
 */
export const PropertyJobsSection = forwardRef<HTMLElement, {
  /** Every job at the property, archived ones included. */
  projects: readonly ApiPropertyProject[];
  propertyIsArchived: boolean;
  getProjectTypeLabel: (projectType?: string | null) => string | null | undefined;
  canCreate: boolean;
  canUpdate: boolean;
  onNewJob: () => void;
  onOpenJob: (project: ApiPropertyProject) => void;
  updateJob: (projectId: string, update: ApiUpdatePropertyProject) => Promise<unknown>;
  styles?: PropertyJobsSectionStyles;
}>(({
  projects,
  propertyIsArchived,
  getProjectTypeLabel,
  canCreate,
  canUpdate,
  onNewJob,
  onOpenJob,
  updateJob,
  styles = DEFAULT_PROPERTY_JOBS_SECTION_STYLES,
}, ref) => {
  const { showErrorToast } = useToast();
  const [view, setView] = useState<PropertyJobsView>('active');
  const [newestFirst, setNewestFirst] = useState(true);
  const counts = getPropertyJobCounts(projects, { propertyIsArchived });
  const rows = getPropertyJobsForView(projects, view, { propertyIsArchived, newestFirst });
  /* Design foots every job at the property, not only the Active/Archived filter. */
  const lifetimeTotal = getPropertyJobsLifetimeTotal(projects);

  const formatScopeAmount = (amount: number | undefined) => amount == null ? null : formatCurrency(amount);
  const formatScopeChanges = (changes: number | undefined) => {
    if (changes == null) return null;
    const formatted = formatCurrency(Math.abs(changes));
    return changes > 0 ? `+${formatted}` : changes < 0 ? `-${formatted}` : formatted;
  };

  const run = (action: string, update: () => Promise<unknown>) => async () => {
    try {
      await update();
    } catch (error) {
      showErrorToast(error, action);
    }
  };
  const menuFor = (project: ApiPropertyProject): RowKebabMenuItem[] => [
    project.archivedAt
      ? { label: 'Unarchive', onSelect: run('unarchive the job', () => updateJob(project.id, { archivedAt: null })) }
      : { label: 'Archive', onSelect: run('archive the job', () => updateJob(project.id, { archivedAt: new Date().toISOString() })) },
    project.status === 'active'
      ? { label: 'Complete Project', onSelect: run('complete the job', () => updateJob(project.id, { status: 'completed' })) }
      : { label: 'Activate Project', onSelect: run('reactivate the job', () => updateJob(project.id, { status: 'active' })) },
  ];

  const stackedDate = (date: Date | string | undefined, soft = false) => {
    const stacked = getStackedDate(date);
    if (!stacked) return <span css={styles.shared.dash}>—</span>;
    return (
      <span css={soft ? styles.softDate : undefined}>
        <span css={soft ? styles.dateDaySoft : styles.dateDay}>{stacked.day}</span>
        <span css={styles.dateYear}>{stacked.year}</span>
      </span>
    );
  };

  return (
    <section ref={ref} css={[styles.shared.section, styles.module]} aria-labelledby='property-jobs-title'>
      <div css={[styles.shared.sectionHeader, styles.moduleHeader]}>
        <h2 id='property-jobs-title' css={[styles.shared.sectionTitle, styles.moduleTitle]}>Jobs</h2>
        {propertyIsArchived ? null : (
          <div css={styles.shared.segments} role='group' aria-label='Which jobs'>
            {(['active', 'archived'] as const).map(option => (
              <button
                key={option}
                type='button'
                css={[styles.shared.segment, view === option && styles.shared.selectedSegment]}
                aria-pressed={view === option}
                onClick={() => setView(option)}
              >
                {option === 'active' ? 'Active' : 'Archived'} {counts[option]}
              </button>
            ))}
          </div>
        )}
        {canCreate
          ? (
            <button type='button' css={[styles.shared.button, styles.shared.primaryButton, propertyIsArchived && { marginLeft: 'auto' }]} onClick={onNewJob}>
              {NEW_JOB_LABEL}
            </button>
          )
          : null}
      </div>

      <div css={styles.tableWrap}>
        <table css={styles.table} role='table'>
          <thead role='rowgroup'>
            <tr role='row'>
              <th css={styles.headerCell} role='columnheader' aria-sort={newestFirst ? 'descending' : 'ascending'}>
                <button type='button' css={styles.sortButton} onClick={() => setNewestFirst(current => !current)}>
                  Updated {newestFirst ? '↓' : '↑'}
                </button>
              </th>
              <th css={[styles.headerCell, styles.spacerCell]} role='presentation' />
              <th css={styles.headerCell} role='columnheader'>Created</th>
              <th css={styles.headerCell} role='columnheader'>Job</th>
              <th css={styles.headerCell} role='columnheader'>Type</th>
              <th css={styles.headerCell} role='columnheader'>Status</th>
              <th css={styles.headerCell} role='columnheader'>Description</th>
              <th css={[styles.headerCell, styles.moneyCell]} role='columnheader'>Initial scope</th>
              <th css={[styles.headerCell, styles.moneyCell]} role='columnheader'>Changes</th>
              <th css={[styles.headerCell, styles.moneyCell, styles.totalCell]} role='columnheader'>Total</th>
              <th css={styles.headerCell} role='columnheader' aria-label='Actions' />
            </tr>
          </thead>
          <tbody role='rowgroup'>
            {rows.length === 0
              ? (
                <tr role='row'>
                  <td css={styles.empty} role='cell'>No {view} jobs on this property.</td>
                </tr>
              )
              : rows.map((project, index) => {
                const scopeMoney = getPropertyJobScopeMoney(project);
                const initialScopeLabel = formatScopeAmount(scopeMoney.initialScope);
                const changesLabel = formatScopeChanges(scopeMoney.changes);
                const totalLabel = formatScopeAmount(scopeMoney.total);

                return (
                  <tr
                    key={project.id}
                    role='row'
                    css={[styles.row, index % 2 === 1 && styles.alternateRow]}
                    data-testid='property-job'
                    onClick={() => onOpenJob(project)}
                  >
                    <td css={[styles.cell, styles.dateCell]} role='cell'>{stackedDate(project.updatedAt)}</td>
                    <td css={[styles.cell, styles.spacerCell]} role='presentation' />
                    <td css={[styles.cell, styles.dateCell]} role='cell'>{stackedDate(project.createdAt, true)}</td>
                    <td css={styles.cell} role='cell'>
                      <button
                        type='button'
                        css={styles.jobName}
                        onClick={event => {
                          event.stopPropagation();
                          onOpenJob(project);
                        }}
                      >
                        {project.name || 'Untitled job'}
                      </button>
                    </td>
                    <td css={[styles.cell, styles.secondaryText]} role='cell'>
                      {getProjectTypeLabel(project.customerMetadata?.projectType) ?? <span css={styles.shared.dash}>—</span>}
                    </td>
                    <td css={styles.cell} role='cell'>
                      <span css={[styles.shared.statusChip, styles.shared.statusChipByStatus[project.status]]}>{STATUS_LABEL[project.status]}</span>
                    </td>
                    <td css={styles.cell} role='cell'>
                      {project.description ? <div css={styles.description} title={project.description}>{project.description}</div> : null}
                    </td>
                    <td css={[styles.cell, styles.moneyCell]} role='cell'>
                      {initialScopeLabel ?? <span css={styles.shared.dash}>—</span>}
                    </td>
                    <td css={[styles.cell, styles.changesCell]} role='cell'>
                      {changesLabel ?? <span css={styles.shared.dash}>—</span>}
                    </td>
                    <td css={[styles.cell, styles.totalCell]} role='cell'>
                      {totalLabel ?? <span css={styles.shared.dash}>—</span>}
                    </td>
                    <td css={[styles.cell, styles.actionsCell]} role='cell'>
                      {canUpdate ? <RowKebabMenu label={project.name || 'job'} items={menuFor(project)} alwaysVisible /> : null}
                    </td>
                  </tr>
                );
              })}
          </tbody>
          {rows.length > 0
            ? (
              <tfoot role='rowgroup'>
                <tr css={styles.footerRow} role='row'>
                  <td css={[styles.cell, styles.footerPad]} role='presentation' />
                  <td css={[styles.cell, styles.footerLabel]} role='cell'>Lifetime</td>
                  <td css={[styles.cell, styles.footerTotal]} role='cell'>{formatCurrency(lifetimeTotal)}</td>
                  <td css={styles.cell} role='presentation' />
                </tr>
              </tfoot>
            )
            : null}
        </table>
      </div>
    </section>
  );
});
PropertyJobsSection.displayName = 'PropertyJobsSection';
